import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowLeft, 
  Target,
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useFrameworksProgress } from '@/hooks/useChangeManagement';
import { supabase } from '@/integrations/supabase/client';

interface FrameworksSectionProps {
  onBack: () => void;
  organizationId: string | null;
}

export function FrameworksSection({ onBack, organizationId }: FrameworksSectionProps) {
  const { phases, loading, overallProgress, toggleTask, addTasks } = useFrameworksProgress(organizationId);
  const [expandedPhase, setExpandedPhase] = useState<number | null>(1);
  const [isGenerating, setIsGenerating] = useState(false);

  const getPhaseProgress = (phase: typeof phases[0]) => {
    if (phase.tasks.length === 0) return 0;
    return Math.round((phase.tasks.filter(t => t.checked).length / phase.tasks.length) * 100);
  };

  const generateAIRecommendations = async (phaseId: number) => {
    if (!organizationId || organizationId === 'all') {
      toast.error('Selecciona una organización primero');
      return;
    }

    setIsGenerating(true);
    try {
      const phase = phases.find(p => p.id === phaseId);
      if (!phase) return;

      const userMessage = `Eres un experto en gestión del cambio organizacional con marcos Kotter y ADKAR. Genera exactamente 2 tareas personalizadas para la fase "${phase.name}" (${phase.description}). Kotter: ${phase.kotterStep}. ADKAR: ${phase.adkarName}. Tareas existentes: ${phase.tasks.map(t => t.text).join('; ')}. Genera 2 tareas adicionales personalizadas que complementen las existentes. Responde SOLO con un JSON array de objetos con campos: "text" (string, la tarea), "recommendation" (string, por qué es relevante). Sin markdown, solo JSON.`;

      const { data, error } = await supabase.functions.invoke('lia-chat', {
        body: {
          message: userMessage,
          context_type: 'change-management'
        }
      });

      if (error) throw error;

      let aiTasks: { text: string; recommendation: string }[] = [];
      try {
        const content = data?.response || '';
        const cleaned = content.replace(/```json\n?|\n?```/g, '').trim();
        aiTasks = JSON.parse(cleaned);
      } catch {
        // Fallback hardcoded
        aiTasks = [
          { text: `Tarea IA personalizada para ${phase.name}`, recommendation: 'Generada por IA basada en el contexto de tu organización.' },
        ];
      }

      const newTasks = aiTasks.map((t, i) => ({
        id: `ai-${phaseId}-${Date.now()}-${i}`,
        text: t.text,
        checked: false,
        aiGenerated: true,
        recommendation: t.recommendation,
      }));

      addTasks(phaseId, newTasks);
      toast.success(`${newTasks.length} tareas personalizadas agregadas`);
    } catch (err) {
      console.error('[ai-recommendations]', err);
      toast.error('Error al generar recomendaciones');
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }


  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="ghost" onClick={onBack} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Volver
      </Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 shadow-lg">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold">Plan de Gestión del Cambio</h1>
            <p className="text-muted-foreground text-sm">Kotter + ADKAR — Checklist de tareas con recomendaciones IA</p>
          </div>
        </div>
        <div className="text-center px-4 py-2 rounded-xl bg-muted/50 border">
          <div className="text-2xl font-bold text-primary">{overallProgress}%</div>
          <div className="text-xs text-muted-foreground">Progreso Global</div>
          <Progress value={overallProgress} className="h-1.5 mt-1 w-28" />
        </div>
      </div>

      {/* Phases */}
      <div className="space-y-4">
        {phases.map((phase) => {
          const progress = getPhaseProgress(phase);
          const isExpanded = expandedPhase === phase.id;
          const checkedCount = phase.tasks.filter(t => t.checked).length;

          return (
            <Card key={phase.id} className={`overflow-hidden transition-all ${isExpanded ? 'ring-1 ring-primary/30' : ''}`}>
              <div className={`h-1.5 bg-gradient-to-r ${phase.color}`} />
              <CardHeader
                className="cursor-pointer py-4"
                onClick={() => setExpandedPhase(isExpanded ? null : phase.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${phase.color} flex items-center justify-center`}>
                      <span className="text-sm font-bold text-white">{phase.adkarLetter}</span>
                    </div>
                    <div>
                      <CardTitle className="text-base">Fase {phase.id}: {phase.name}</CardTitle>
                      <CardDescription className="text-xs">{phase.kotterStep} · {phase.adkarName}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{checkedCount}/{phase.tasks.length}</span>
                    <div className="text-right">
                      <span className={`text-sm font-bold ${progress === 100 ? 'text-emerald-500' : 'text-primary'}`}>{progress}%</span>
                      <Progress value={progress} className="h-1.5 w-20 mt-0.5" />
                    </div>
                  </div>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0 pb-4 space-y-3">
                  <p className="text-sm text-muted-foreground mb-3">{phase.description}</p>
                  <div className="space-y-2">
                    {phase.tasks.map((task) => (
                      <div
                        key={task.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                          task.checked ? 'bg-muted/30 border-border/30' : 'bg-background border-border/50 hover:border-primary/30'
                        }`}
                      >
                        <Checkbox checked={task.checked} onCheckedChange={() => toggleTask(phase.id, task.id)} className="mt-0.5" />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm ${task.checked ? 'line-through text-muted-foreground' : ''}`}>{task.text}</span>
                            {task.aiGenerated && (
                              <Badge variant="secondary" className="text-xs shrink-0">
                                <Sparkles className="w-3 h-3 mr-0.5" /> IA
                              </Badge>
                            )}
                          </div>
                          {task.recommendation && (
                            <p className="text-xs text-primary/80 italic">💡 {task.recommendation}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={(e) => { e.stopPropagation(); generateAIRecommendations(phase.id); }}
                    disabled={isGenerating}
                  >
                    {isGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generar tareas personalizadas con IA
                  </Button>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
